import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Edit, PlusCircle, Trash2 } from 'react-feather';
import { Card, CardBody, CardHeader, Col, Row, Container, Media } from 'reactstrap';
import DataTable from '../../Common/DataTable';
import { Breadcrumbs, H5 } from '../../../AbstractElements';
import AnnouncementDataModal from './AnnouncementDataModal';
import DeleteConfirmationModal from '../../Common/Modals/DeleteConfirmationModal';
import { toast } from 'react-toastify';
import { confirmDelete, fetchTableData } from '../../../Api/ApiMethods';

const Announcements = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [data, setData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(true);

    const announcementApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/announcement';

    useEffect(() => {
        let isMounted = true;
        const loadAnnouncements = async () => {
            setIsLoading(true);
            try {
                await fetchTableData({
                    apiEndpoint: announcementApiUrl,
                    page,
                    pageSize,
                    setDataCallback: setData,
                    setTotalRowsCallback: setTotalRows,
                });
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadAnnouncements();

        return () => {
            isMounted = false;
        };
    }, [page, pageSize, announcementApiUrl]);

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const toggleDeleteModal = () => {
        if (selectedRows.length > 0) {
            setDeleteModalOpen(!deleteModalOpen);
        } else {
            toast.error("Lütfen silmek için en az bir duyuru seçin.");
        }
    }

    const openAddModal = () => {
        setModalMode('add');
        setSelectedAnnouncement(null);
        setModalOpen(true);
    };

    const openModifyModal = (announcement) => {
        setModalMode('modify');
        setSelectedAnnouncement(announcement);
        setModalOpen(true);
    };

    const toggleDataModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setSelectedAnnouncement(null);
        }
    }

    const handleDeleteConfirm = async () => {
        await confirmDelete({
            apiEndpoint: announcementApiUrl,
            selectedItems: selectedRows,
            toggleModalCallback: toggleDeleteModal,
            clearSelectionCallback: () => {
                setSelectedRows([]);
                setClearSelectedRows(!clearSelectedRows);
            },
            refreshDataCallback: () => fetchTableData({
                apiEndpoint: announcementApiUrl,
                page,
                pageSize,
                setDataCallback: setData,
                setTotalRowsCallback: setTotalRows,
            })
        });
    };

    const columns = [
        {
            name: 'Başlık',
            selector: row => row.title,
            sortable: true,
            wrap: true
        },
        {
            name: 'Açıklama',
            selector: row => row.text,
            sortable: false,
            wrap: true,
            cell: row => {
                if (!row.text) return <div style={{ whiteSpace: 'normal' }}>-</div>;
                const truncatedDesc = row.text.length > 50 ? row.text.substring(0, 50) + '...' : row.text;
                return <div style={{ whiteSpace: 'normal' }} title={row.text}>{truncatedDesc}</div>;
            }
        },
        {
            name: 'Durum',
            selector: row => row.isActive,
            sortable: true,
            cell: row => row.isActive
                ? <span className="badge badge-light-success text-center" style={{ borderRadius: '8px', padding: '0.6em 1em', display: 'inline-block', minWidth: '50px' }}>Aktif</span>
                : <span className="badge badge-light-danger text-center" style={{ borderRadius: '8px', padding: '0.6em 1em', display: 'inline-block', minWidth: '50px' }}>Pasif</span>
        },
        {
            name: 'Düzenle',
            cell: row => (
                <div className="btn btn-outline-warning" onClick={() => openModifyModal(row)}>
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true
        },
    ];

    return (
        <>
            <Breadcrumbs parent='Duyuru Alanı' title='Duyurular' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Duyurular</H5>
                                <span>Tanımlanmış olan duyurular aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                    <Media body className='text-end mt-3'>
                                        <div className="btn btn-outline-primary ms-2" onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <PlusCircle size={18} className="me-1" />
                                            {' Duyuru Ekle'}
                                        </div>
                                    </Media>
                                    {(selectedRows.length !== 0) &&
                                        <div className='btn btn-outline-danger ms-2 mt-3' onClick={toggleDeleteModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <Trash2 size={18} className="me-1" />
                                            {' Seçilenleri Sil'}
                                        </div>
                                    }
                                </div>

                                <div style={{ height: "40px" }}></div>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        onSelectedRowsChange={handleRowSelected}
                                        pagination
                                        paginationServer
                                        selectableRows
                                        clearSelectedRows={clearSelectedRows}
                                        paginationTotalRows={totalRows}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={pageSize => { setPage(1); setPageSize(pageSize); }}
                                        progressPending={isLoading}
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={isLoading ? null : <div className="p-4 text-center">Duyuru bulunmamaktadır.</div>}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <AnnouncementDataModal
                apiEndpoint={announcementApiUrl}
                isOpen={modalOpen}
                toggle={toggleDataModal}
                mode={modalMode}
                selectedAnnouncement={selectedAnnouncement}
                refreshData={() => fetchTableData({
                    apiEndpoint: announcementApiUrl,
                    page,
                    pageSize,
                    setDataCallback: setData,
                    setTotalRowsCallback: setTotalRows,
                })}
            />

            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                toggle={toggleDeleteModal}
                itemsToDelete={selectedRows}
                onConfirm={handleDeleteConfirm}
                itemName="duyuruyu"
                displayKey="title"
            />
        </>
    );
};

export default Announcements;